/**
 * Visual Props Resolver — Extracts and normalizes props for each visual type.
 *
 * Extracted from VisualAidFactory.tsx to keep the factory thin (<50 lines).
 * Each visual type has specific prop extraction logic that maps raw seed `data`
 * to the component's expected props interface.
 */

import { resolveFractionVisualType } from '../fractionVisualTypeResolver'

type DataRecord = Record<string, unknown>

// ─── Data extraction helpers ──────────────────────────────────────────────────

function getString(data: DataRecord, key: string): string | undefined {
  const v = data[key]
  return typeof v === 'string' ? v : undefined
}

function getNumber(data: DataRecord, key: string): number | undefined {
  const v = data[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

function getBoolean(data: DataRecord, key: string): boolean | undefined {
  const v = data[key]
  return typeof v === 'boolean' ? v : undefined
}

function getRecord(
  data: DataRecord,
  key: string,
): Record<string, unknown> | undefined {
  const v = data[key]
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined
}

function getArray(data: DataRecord, key: string): unknown[] | undefined {
  const v = data[key]
  return Array.isArray(v) ? v : undefined
}

// ─── Type alias normalization ─────────────────────────────────────────────────

/**
 * Normalize visual type aliases to canonical names.
 */
export function normalizeVisualType(type: string): string {
  if (type === 'numberLine') return 'numberline'
  if (type === 'moneyCoins') return 'money'
  if (type === 'analog_clock') return 'clock'
  if (type === 'digital_clock') return 'digitalClock'
  if (type === 'fraction_bar' || type === 'fraction_circle') return 'fraction'
  return type
}

// ─── Per-type prop resolvers ──────────────────────────────────────────────────

type PropResolver = (
  data: DataRecord,
  mode: string,
) => Record<string, unknown> | null

const resolvers: Record<string, PropResolver> = {
  stairs(data, mode) {
    const stairsType = getString(data, 'type')
    if (!stairsType) return null
    // Normalize Dutch / legacy aliases to the canonical English keys that
    // MetricStairsCompact's METRIC_UNITS lookup expects. An unknown value
    // would otherwise yield `undefined` units and crash on .indexOf().
    const STAIRS_TYPE_ALIASES: Record<string, 'length' | 'volume' | 'weight'> =
      {
        length: 'length',
        volume: 'volume',
        weight: 'weight',
        lengte: 'length',
        inhoud: 'volume',
        gewicht: 'weight',
        massa: 'weight',
      }
    const canonicalType = STAIRS_TYPE_ALIASES[stairsType.toLowerCase()]
    if (!canonicalType) return null
    return {
      type: canonicalType,
      from: getString(data, 'from'),
      to: getString(data, 'to'),
      value: getNumber(data, 'value') ?? 0,
      highlight: getString(data, 'highlight'),
      showAnimation: getBoolean(data, 'showAnimation') ?? false,
      mode,
    }
  },

  circle(data, mode) {
    return {
      radius: getNumber(data, 'radius'),
      diameter: getNumber(data, 'diameter'),
      circumference: getNumber(data, 'circumference'),
      area: getNumber(data, 'area'),
      label: getString(data, 'label'),
      showFormula: getBoolean(data, 'showFormula') ?? false,
      highlightRadius: getBoolean(data, 'highlightRadius') ?? false,
      highlightDiameter: getBoolean(data, 'highlightDiameter') ?? false,
      sectorAngle: getNumber(data, 'sectorAngle'),
      highlightSector: getBoolean(data, 'highlightSector') ?? false,
      mode,
    }
  },

  fraction(data, mode) {
    const denominator = getNumber(data, 'denominator') ?? 4
    if (denominator === 0) return null
    const value = getNumber(data, 'value') ?? 0
    const numerator =
      getNumber(data, 'numerator') ?? Math.round(value * denominator)
    const rawVisualType = getString(data, 'visualType')
    const explicitType =
      rawVisualType === 'pizza' || rawVisualType === 'bar'
        ? (rawVisualType as 'pizza' | 'bar')
        : undefined
    const questionText = getString(data, 'questionText') ?? ''
    const options = getArray(data, 'options') as string[] | undefined
    const visualType = resolveFractionVisualType(
      questionText,
      options,
      explicitType,
    )
    return {
      numerator,
      denominator,
      label: getString(data, 'label'),
      type: visualType,
      showEaten: getBoolean(data, 'showEaten') ?? false,
      mode,
      fractions: getArray(data, 'fractions') as
        | Array<{
            numerator: number
            denominator: number
            label?: string
            visualType?: string
          }>
        | undefined,
      operator: (getString(data, 'operator') as '+' | '-' | '×' | '÷') ?? '+',
    }
  },

  geo(data, mode) {
    const width = getNumber(data, 'width')
    const height = getNumber(data, 'height')
    if (!width || !height) return null
    return {
      width,
      height,
      depth: getNumber(data, 'depth'),
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid'),
      unit: getString(data, 'unit'),
      mode,
    }
  },

  numberline(data, mode) {
    const start = getNumber(data, 'start')
    const end = getNumber(data, 'end')

    if (start === undefined || end === undefined) return null

    // Accept multiple aliases for the highlighted-values array. Seeds in the
    // wild use `marked`, `markers`, `highlight`, or even `value` (singular).
    // Merging all of them into a single highlight set is defensive: the
    // renderer treats them as visually equivalent indicator dots.
    const highlightRaw = getArray(data, 'highlight') as number[] | undefined
    const markedRaw = getArray(data, 'marked') as number[] | undefined
    const markersRaw = getArray(data, 'markers') as number[] | undefined
    const valueSingle = getNumber(data, 'value')

    const highlightSet = new Set<number>()
    const addAll = (arr: number[] | undefined) => {
      if (!arr) return
      arr.forEach((v) => {
        if (typeof v === 'number' && Number.isFinite(v)) highlightSet.add(v)
      })
    }
    addAll(highlightRaw)
    addAll(markedRaw)
    addAll(markersRaw)
    if (typeof valueSingle === 'number') highlightSet.add(valueSingle)

    // Arrows: drawn above the line as labelled jumps (e.g. negative numbers).
    const arrowsRaw = getArray(data, 'arrows')
    const arrows = arrowsRaw
      ? (arrowsRaw
          .map((item) => {
            if (!item || typeof item !== 'object') return null
            const obj = item as Record<string, unknown>
            const from = typeof obj.from === 'number' ? obj.from : undefined
            const to = typeof obj.to === 'number' ? obj.to : undefined
            if (from === undefined || to === undefined) return null
            const label = typeof obj.label === 'string' ? obj.label : undefined
            return { from, to, label }
          })
          .filter(Boolean) as Array<{
          from: number
          to: number
          label?: string
        }>)
      : undefined

    return {
      start,
      end,
      step: getNumber(data, 'step'),
      highlight: Array.from(highlightSet),
      label: getString(data, 'label'),
      tickLabels: getArray(data, 'tickLabels') as string[] | undefined,
      fractionLabels: getRecord(data, 'fractionLabels') as
        | Record<string, string>
        | undefined,
      arrows: arrows && arrows.length > 0 ? arrows : undefined,
      mode,
    }
  },

  ratio(data, mode) {
    const headers = getArray(data, 'headers')
    const rows = getArray(data, 'rows')
    if (!headers || !rows) return null
    return { ...(data as Record<string, unknown>), mode }
  },

  rule(data, mode) {
    const title = getString(data, 'title')
    const content = getString(data, 'content')
    if (!title || !content) return null
    return { ...(data as Record<string, unknown>), mode }
  },

  money(data, mode) {
    const amount = getNumber(data, 'amount')
    if (amount === undefined) return null
    return {
      amount,
      breakdown: getRecord(data, 'breakdown'),
      showCoins: data['showCoins'] !== false,
      showBills: data['showBills'] !== false,
      interactive: getBoolean(data, 'interactive') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  column(data, mode) {
    const operation = getString(data, 'operation')
    if (!operation) return null
    const operand1 = data['operand1'] ?? getArray(data, 'numbers')?.[0]
    const operand2 = data['operand2'] ?? getArray(data, 'numbers')?.[1]
    if (operand1 === undefined || operand2 === undefined) return null
    return {
      operation,
      operand1,
      operand2,
      showSteps: data['showSteps'] !== false,
      currentStep: getNumber(data, 'currentStep') ?? 0,
      animated: data['animated'] !== false,
      mode,
    }
  },

  clock(data, mode) {
    return {
      hours: getNumber(data, 'hours') ?? getNumber(data, 'hour') ?? 12,
      minutes: getNumber(data, 'minutes') ?? getNumber(data, 'minute') ?? 0,
      showSeconds: false,
      label: getString(data, 'label'),
      mode,
    }
  },

  digitalClock(data, mode) {
    return {
      hours: getNumber(data, 'hours') ?? getNumber(data, 'hour') ?? 12,
      minutes: getNumber(data, 'minutes') ?? getNumber(data, 'minute') ?? 0,
      seconds: getNumber(data, 'seconds'),
      showSeconds: getBoolean(data, 'showSeconds') ?? false,
      format: (getString(data, 'format') as '12h' | '24h') ?? '24h',
      label: getString(data, 'label'),
      mode,
    }
  },

  stopwatch(data, mode) {
    return {
      display: getString(data, 'display') ?? '00:00',
      precision: getString(data, 'precision') as
        | 'seconds'
        | 'tenths'
        | 'hundredths'
        | undefined,
      variant: getString(data, 'variant') as
        | 'single'
        | 'laps'
        | 'compare'
        | 'decimal'
        | undefined,
      laps: getArray(data, 'laps') as
        | Array<{
            lapNumber: number
            splitTime: string
            cumulativeTime: string
          }>
        | undefined,
      compare: getRecord(data, 'compare') as
        | { time1: string; time2: string }
        | undefined,
      highlight: getString(data, 'highlight') as
        | 'faster'
        | 'slower'
        | null
        | undefined,
      showDial: getBoolean(data, 'showDial') ?? mode === 'support',
      label: getString(data, 'label'),
      mode,
    }
  },

  kofschipMnemonic(data, mode) {
    return {
      verb: getString(data, 'verb') ?? '',
      stem: getString(data, 'stem') ?? '',
      lastLetter: getString(data, 'lastLetter') ?? '',
      useTE: getBoolean(data, 'useTE') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  syllableBlocks(data, mode) {
    const syllables = getArray(data, 'syllables') as string[] | undefined
    const types = getArray(data, 'types') as ('open' | 'closed')[] | undefined
    if (!syllables || !types) return null
    return {
      word: getString(data, 'word') ?? '',
      syllables,
      types,
      highlight: getNumber(data, 'highlight'),
      label: getString(data, 'label'),
      mode,
    }
  },

  verbTable(data, mode) {
    const persons = getArray(data, 'persons') as string[] | undefined
    const forms = getArray(data, 'forms') as string[] | undefined
    if (!persons || !forms) return null
    return {
      verb: getString(data, 'verb') ?? '',
      tense:
        (getString(data, 'tense') as 'present' | 'past' | 'perfect') ??
        'present',
      persons,
      forms,
      highlight: getString(data, 'highlight'),
      label: getString(data, 'label'),
      mode,
    }
  },

  coordinateGrid(data, mode) {
    return {
      width: getNumber(data, 'width') ?? 10,
      height: getNumber(data, 'height') ?? 10,
      points: getArray(data, 'points') as
        | Array<{
            x: number
            y: number
            label: string
            color?: string
          }>
        | undefined,
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      mode,
    }
  },

  box3d(data, mode) {
    return {
      length: getNumber(data, 'length') ?? getNumber(data, 'width') ?? 5,
      width: getNumber(data, 'depth') ?? getNumber(data, 'width') ?? 5,
      height: getNumber(data, 'height') ?? 5,
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      mode,
    }
  },

  compositeShape(data, mode) {
    const rectangles = getArray(data, 'rectangles') as
      | Array<{
          x: number
          y: number
          width: number
          height: number
          label?: string
        }>
      | undefined
    if (!rectangles || rectangles.length === 0) return null
    return {
      rectangles,
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      showDimensions: getBoolean(data, 'showDimensions') ?? true,
      mode,
    }
  },

  readingPassage(data, mode) {
    const passage = getString(data, 'passage')
    if (!passage) return null
    return {
      passage,
      instruction: getString(data, 'instruction'),
      source: getString(data, 'source'),
      label: getString(data, 'label'),
      mode,
    }
  },

  geometricShape(data, mode) {
    const shape = getString(data, 'shape') as
      | 'rectangle'
      | 'triangle'
      | 'parallelogram'
      | 'trapezoid'
      | undefined
    const base = getNumber(data, 'base')
    const height = getNumber(data, 'height')
    if (!shape || !base || !height) return null
    return {
      shape,
      base,
      height,
      sideLength: getNumber(data, 'sideLength'),
      topBase: getNumber(data, 'topBase'),
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      showGrid: getBoolean(data, 'showGrid') ?? true,
      showDimensions: getBoolean(data, 'showDimensions') ?? true,
      hideHeight: getBoolean(data, 'hideHeight') ?? false,
      mode,
    }
  },

  barChart(data, mode) {
    const chartData = getArray(data, 'data') as
      | Array<{ label: string; value: number }>
      | undefined
    if (!chartData || chartData.length === 0) return null
    return {
      data: chartData,
      xAxisLabel: getString(data, 'xAxisLabel'),
      yAxisLabel: getString(data, 'yAxisLabel'),
      barColor: getString(data, 'barColor'),
      hideValues: getBoolean(data, 'hideValues') ?? true,
      mode,
    }
  },

  lineChart(data, mode) {
    const chartData = getArray(data, 'data') as
      | Array<{ label: string; value: number }>
      | undefined
    if (!chartData || chartData.length === 0) return null
    return {
      data: chartData,
      xAxisLabel: getString(data, 'xAxisLabel'),
      yAxisLabel: getString(data, 'yAxisLabel'),
      lineColor: getString(data, 'lineColor'),
      hideValues: getBoolean(data, 'hideValues') ?? true,
      mode,
    }
  },

  calendar(data, mode) {
    const year = getNumber(data, 'year')
    const month = getNumber(data, 'month')
    if (!year || !month) return null
    return {
      year,
      month,
      highlightDates: getArray(data, 'highlightDates') as number[] | undefined,
      todayDate: getNumber(data, 'todayDate'),
      targetDate: getNumber(data, 'targetDate'),
      secondMonth: getNumber(data, 'secondMonth'),
      secondMonthYear: getNumber(data, 'secondMonthYear'),
      secondMonthHighlight: getArray(data, 'secondMonthHighlight') as
        | number[]
        | undefined,
      secondMonthTarget: getNumber(data, 'secondMonthTarget'),
      label: getString(data, 'label'),
      mode,
    }
  },

  thermometer(data, mode) {
    const currentTemp = getNumber(data, 'currentTemp')
    if (currentTemp === undefined) return null
    return {
      minTemp: getNumber(data, 'minTemp') ?? -20,
      maxTemp: getNumber(data, 'maxTemp') ?? 50,
      currentTemp,
      targetTemp: getNumber(data, 'targetTemp'),
      unit: getString(data, 'unit') ?? 'C',
      label: getString(data, 'label'),
      mode,
    }
  },

  ruler(data, mode) {
    return {
      length: getNumber(data, 'length') ?? 15,
      objectStart: getNumber(data, 'objectStart'),
      objectEnd: getNumber(data, 'objectEnd'),
      unit: getString(data, 'unit') ?? 'cm',
      label: getString(data, 'label'),
      hideValue: getBoolean(data, 'hideValue') ?? false,
      mode,
    }
  },

  measuringCup(data, mode) {
    const maxVolume = getNumber(data, 'maxVolume')
    const currentVolume = getNumber(data, 'currentVolume')
    if (!maxVolume || currentVolume === undefined) return null
    return {
      maxVolume,
      currentVolume,
      targetVolume: getNumber(data, 'targetVolume'),
      unit: getString(data, 'unit') ?? 'ml',
      label: getString(data, 'label'),
      hideValue: getBoolean(data, 'hideValue') ?? false,
      mode,
    }
  },

  weighScale(data, mode) {
    const leftWeight = getNumber(data, 'leftWeight')
    const rightWeight = getNumber(data, 'rightWeight')
    if (leftWeight === undefined || rightWeight === undefined) return null
    return {
      leftWeight,
      rightWeight,
      leftLabel: getString(data, 'leftLabel'),
      rightLabel: getString(data, 'rightLabel'),
      unit: getString(data, 'unit') ?? 'kg',
      label: getString(data, 'label'),
      showQuestion: getBoolean(data, 'showQuestion') ?? false,
      mode,
    }
  },

  equalSharing(data, mode) {
    const total = getNumber(data, 'total')
    const parts = getNumber(data, 'parts')
    const perPart = getNumber(data, 'perPart')
    if (total === undefined || parts === undefined || perPart === undefined)
      return null
    return {
      total,
      parts,
      perPart,
      unit: getString(data, 'unit'),
      label: getString(data, 'label'),
      mode,
    }
  },

  percentageBar(data, mode) {
    const percentage = getNumber(data, 'percentage')
    if (percentage === undefined) return null
    return {
      percentage,
      comparePercentage: getNumber(data, 'comparePercentage'),
      percentageLabel: getString(data, 'percentageLabel'),
      compareLabel: getString(data, 'compareLabel'),
      showValue: getBoolean(data, 'showValue') ?? true,
      label: getString(data, 'label'),
      mode,
    }
  },

  fractionStrip(data, mode) {
    const fractions = getArray(data, 'fractions') as
      | Array<{ numerator: number; denominator: number; label?: string }>
      | undefined
    if (!fractions || fractions.length === 0) return null
    return {
      fractions,
      highlightIndex: getNumber(data, 'highlightIndex'),
      label: getString(data, 'label'),
      mode,
    }
  },

  splitDiagram(data, mode) {
    const original = getNumber(data, 'original')
    const parts = getArray(data, 'parts') as
      | Array<{ value: number; label?: string }>
      | undefined
    if (original === undefined || !parts || parts.length === 0) return null
    return {
      original,
      parts,
      operation: getString(data, 'operation') ?? 'add',
      operand: getNumber(data, 'operand'),
      result: getNumber(data, 'result'),
      label: getString(data, 'label'),
      mode,
    }
  },

  strategySteps(data, mode) {
    // StrategySteps — a numbered, didactic step-chain (splitsen,
    // compenseren, rijgen, …). Requires a non-empty `steps` array;
    // each entry must carry at least a `label` string. Optional
    // `description` and `highlight` per step are preserved.
    const rawSteps = getArray(data, 'steps')
    if (!rawSteps || rawSteps.length === 0) return null
    const steps: Array<{
      label: string
      description?: string
      highlight?: boolean
    }> = []
    for (const item of rawSteps) {
      if (!item || typeof item !== 'object') continue
      const obj = item as Record<string, unknown>
      const label = typeof obj.label === 'string' ? obj.label : undefined
      if (!label) continue
      const description =
        typeof obj.description === 'string' ? obj.description : undefined
      const highlight =
        typeof obj.highlight === 'boolean' ? obj.highlight : undefined
      steps.push({ label, description, highlight })
    }
    if (steps.length === 0) return null
    return {
      steps,
      title: getString(data, 'title'),
      label: getString(data, 'label'),
      mode,
    }
  },

  sentenceDiagram(data, mode) {
    const sentence = getString(data, 'sentence')
    const parts = getArray(data, 'parts') as
      | Array<{ text: string; role: string; abbr?: string }>
      | undefined
    if (!sentence || !parts || parts.length === 0) return null
    return {
      sentence,
      parts,
      label: getString(data, 'label'),
      mode,
    }
  },

  mapScale(data, mode) {
    const scaleRatio = getNumber(data, 'scaleRatio')
    if (scaleRatio === undefined) return null
    return {
      scaleRatio,
      mapLength: getNumber(data, 'mapLength'),
      realLength: getNumber(data, 'realLength'),
      unit: getString(data, 'unit') ?? 'km',
      label: getString(data, 'label'),
      mode,
    }
  },

  protractor(data, mode) {
    const angle = getNumber(data, 'angle')
    if (angle === undefined) return null
    return {
      angle,
      compareAngle: getNumber(data, 'compareAngle'),
      showValue: getBoolean(data, 'showValue') ?? true,
      angleType: getString(data, 'angleType'),
      label: getString(data, 'label'),
      mode,
    }
  },

  symmetryGrid(data, mode) {
    return {
      rows: getNumber(data, 'rows') ?? 6,
      cols: getNumber(data, 'cols') ?? 6,
      shadedCells: getArray(data, 'shadedCells') as number[] | undefined,
      axis: getString(data, 'axis') ?? 'vertical',
      showMirror: getBoolean(data, 'showMirror') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  numberBoard(data, mode) {
    return {
      start: getNumber(data, 'start') ?? 1,
      end: getNumber(data, 'end') ?? 100,
      highlight: getArray(data, 'highlight') as number[] | undefined,
      marked: getArray(data, 'marked') as number[] | undefined,
      columns: getNumber(data, 'columns') ?? 10,
      showPattern: getBoolean(data, 'showPattern') ?? false,
      label: getString(data, 'label'),
      mode,
    }
  },

  speedDiagram(data, mode) {
    return {
      speed: getNumber(data, 'speed'),
      distance: getNumber(data, 'distance'),
      time: getNumber(data, 'time'),
      speedUnit: getString(data, 'speedUnit') ?? 'km/u',
      distanceUnit: getString(data, 'distanceUnit') ?? 'km',
      timeUnit: getString(data, 'timeUnit') ?? 'uur',
      unknown: getString(data, 'unknown'),
      label: getString(data, 'label'),
      mode,
    }
  },

  priceCompare(data, mode) {
    const items = getArray(data, 'items') as
      | Array<{
          name: string
          quantity: number
          unit: string
          totalPrice: number
        }>
      | undefined
    if (!items || items.length < 2) return null
    return {
      items,
      compareUnit: getString(data, 'compareUnit') ?? 'per stuk',
      label: getString(data, 'label'),
      mode,
    }
  },

  timeline(data, mode) {
    const events = getArray(data, 'events') as
      | Array<{
          position: number
          label: string
          description?: string
          isHighlighted?: boolean
        }>
      | undefined
    if (!events || events.length === 0) return null
    return {
      events,
      start: getNumber(data, 'start'),
      end: getNumber(data, 'end'),
      label: getString(data, 'label'),
      mode,
    }
  },

  worldClockPanel(data, mode) {
    const clocks = getArray(data, 'clocks') as
      | Array<{
          city: string
          time: string
          utcOffset: number
          isReference?: boolean
        }>
      | undefined
    if (!clocks || clocks.length === 0) return null
    return {
      clocks,
      label: getString(data, 'label'),
      mode,
    }
  },

  receipt(data, mode) {
    const items = getArray(data, 'items') as
      | Array<{ name: string; price: number; quantity?: number }>
      | undefined
    if (!items || items.length === 0) return null
    return {
      items,
      storeName: getString(data, 'storeName'),
      showTotal: getBoolean(data, 'showTotal') ?? true,
      totalBudget: getNumber(data, 'totalBudget'),
      label: getString(data, 'label'),
      mode,
    }
  },

  budgetBar(data, mode) {
    const totalBudget = getNumber(data, 'totalBudget')
    if (!totalBudget) return null
    const segments = getArray(data, 'segments') as
      | Array<{ label: string; amount: number; color?: string }>
      | undefined
    if (!segments || segments.length === 0) return null
    return {
      totalBudget,
      segments,
      showRemaining: getBoolean(data, 'showRemaining') ?? true,
      label: getString(data, 'label'),
      mode,
    }
  },

  wordClassChip(data, mode) {
    return {
      highlightClass: getString(data, 'highlightClass'),
      showLegend: getBoolean(data, 'showLegend') ?? true,
      label: getString(data, 'label'),
      mode,
    }
  },

  signalWordCard(data, mode) {
    const word = getString(data, 'word')
    const fn = getString(data, 'function')
    if (!word || !fn) return null
    return {
      word,
      function: fn,
      example: getString(data, 'example'),
      label: getString(data, 'label'),
      mode,
    }
  },

  synonymAntonymPair(data, mode) {
    const leftWord = getString(data, 'leftWord')
    const rightWord = getString(data, 'rightWord')
    const relation = getString(data, 'relation')
    if (!leftWord || !rightWord || !relation) return null
    return {
      leftWord,
      rightWord,
      relation,
      distractorWord: getString(data, 'distractorWord'),
      label: getString(data, 'label'),
      mode,
    }
  },

  textStructureSchema(data, mode) {
    const structure = getString(data, 'structure')
    if (!structure) return null
    const allowed = [
      'probleem-oplossing',
      'oorzaak-gevolg',
      'vergelijking',
      'chronologisch',
    ]
    if (!allowed.includes(structure)) return null
    const labels = getArray(data, 'labels') as string[] | undefined
    return {
      structure,
      labels: Array.isArray(labels) ? labels : undefined,
      highlightIndex: getNumber(data, 'highlightIndex'),
      label: getString(data, 'label'),
      mode,
    }
  },

  referenceArrow(data, mode) {
    const sentence = getString(data, 'sentence')
    const pronounIndex = getNumber(data, 'pronounIndex')
    const referentIndex = getNumber(data, 'referentIndex')
    if (!sentence) return null
    if (typeof pronounIndex !== 'number' || typeof referentIndex !== 'number')
      return null
    if (pronounIndex === referentIndex) return null
    return {
      sentence,
      pronounIndex,
      referentIndex,
      label: getString(data, 'label'),
      mode,
    }
  },

  contextSpotlight(data, mode) {
    const passage = getString(data, 'passage')
    const targetWord = getString(data, 'targetWord')
    if (!passage || !targetWord) return null
    return {
      passage,
      targetWord,
      gloss: getString(data, 'gloss'),
      source: getString(data, 'source'),
      label: getString(data, 'label'),
      mode,
    }
  },

  topoMap(data, mode) {
    const mapId = getString(data, 'mapId')
    if (!mapId) return null
    const highlightRegionIds = getArray(data, 'highlightRegionIds') as
      | string[]
      | undefined
    const rawStyle = getString(data, 'highlightStyle')
    const highlightStyle =
      rawStyle === 'primary' ||
      rawStyle === 'secondary' ||
      rawStyle === 'subtle'
        ? rawStyle
        : undefined
    return {
      data: {
        mapId,
        highlightRegionIds,
        highlightStyle,
        label: getString(data, 'label'),
      },
      mode,
    }
  },

  squareGrid(data, mode) {
    const size = getNumber(data, 'size')
    if (size === undefined || size < 1) return null
    const rawMode = getString(data, 'mode')
    const gridMode =
      rawMode === 'power' || rawMode === 'root' || rawMode === 'partial'
        ? rawMode
        : 'power'
    return {
      size,
      mode: gridMode,
      targetValue: getNumber(data, 'targetValue'),
      label: getString(data, 'label'),
    }
  },

  placeValueBlocks(data) {
    // F-PVB-TENTHOUSANDS (mei 2026): tenThousands optionally passed through
    // so 5-digit numbers (e.g. 78.432) can be represented faithfully.
    const tenThousands = getNumber(data, 'tenThousands') ?? 0
    const thousands = getNumber(data, 'thousands') ?? 0
    const hundreds = getNumber(data, 'hundreds') ?? 0
    const tens = getNumber(data, 'tens') ?? 0
    const ones = getNumber(data, 'ones') ?? 0
    if (tenThousands + thousands + hundreds + tens + ones === 0) return null
    const rawHi = getString(data, 'highlightColumn')
    const highlightColumn =
      rawHi === 'tenThousands' ||
      rawHi === 'thousands' ||
      rawHi === 'hundreds' ||
      rawHi === 'tens' ||
      rawHi === 'ones'
        ? rawHi
        : null
    const rawSize = getString(data, 'size')
    const size =
      rawSize === 'sm' || rawSize === 'md' || rawSize === 'lg' ? rawSize : 'md'
    return {
      tenThousands,
      thousands,
      hundreds,
      tens,
      ones,
      showLabels: getBoolean(data, 'showLabels') ?? true,
      showTotal: getBoolean(data, 'showTotal') ?? false,
      highlightColumn,
      size,
    }
  },

  barModel(data) {
    const total = data['total']
    const segments = getNumber(data, 'segments')
    if (total === undefined || segments === undefined || segments < 1)
      return null
    const rawSize = getString(data, 'size')
    const size =
      rawSize === 'sm' || rawSize === 'md' || rawSize === 'lg' ? rawSize : 'md'
    return {
      total: total as number | string,
      totalLabel: getString(data, 'totalLabel'),
      unit: getString(data, 'unit'),
      segments,
      highlighted: getArray(data, 'highlighted') as number[] | undefined,
      showUnknownMarker: getBoolean(data, 'showUnknownMarker') ?? false,
      showSegmentValues: getBoolean(data, 'showSegmentValues') ?? false,
      segmentValues: getArray(data, 'segmentValues') as
        | Array<{ value?: number | string; label?: string }>
        | undefined,
      caption: getString(data, 'caption'),
      size,
      label: getString(data, 'label'),
    }
  },

  flowchart(data, mode) {
    const nodes = getArray(data, 'nodes') as
      | Array<{
          id: string
          shape: 'start' | 'end' | 'action' | 'decision'
          label: string
          highlight?: boolean
        }>
      | undefined
    if (!nodes || nodes.length === 0) return null
    return {
      nodes,
      edges: getArray(data, 'edges') as
        | Array<{ from: string; to: string; label?: string }>
        | undefined,
      label: getString(data, 'label'),
      mode,
    }
  },

  mindmap(data, mode) {
    const topic = getString(data, 'topic')
    const branches = getArray(data, 'branches') as
      | Array<{ label: string; leaves?: string[]; highlight?: boolean }>
      | undefined
    if (!topic && (!branches || branches.length === 0)) return null
    return {
      topic,
      branches: branches ?? [],
      highlightCenter: getBoolean(data, 'highlightCenter') ?? false,
      showLeaves: getBoolean(data, 'showLeaves') ?? true,
      label: getString(data, 'label'),
      mode,
    }
  },

  tableOfContents(data, mode) {
    const entries = getArray(data, 'entries') as
      | Array<{
          number?: string
          title: string
          page: number | string
          indent?: number
          highlight?: boolean
        }>
      | undefined
    if (!entries || entries.length === 0) return null
    return {
      title: getString(data, 'title'),
      bookTitle: getString(data, 'bookTitle'),
      entries,
      label: getString(data, 'label'),
      mode,
    }
  },

  compensationNumberLine(data, mode) {
    // Annotated number line with two arcs: a big rounded jump and a small
    // correction jump. Used for the "compenseren" (compensation) strategy in
    // mental arithmetic, e.g. 47 + 39 → 47 + 40 − 1 = 86.
    const start = getNumber(data, 'start')
    const end = getNumber(data, 'end')
    const intermediate = getNumber(data, 'intermediate')
    const mainJumpLabel = getString(data, 'mainJumpLabel')
    const correctionLabel = getString(data, 'correctionLabel')
    if (
      start === undefined ||
      end === undefined ||
      intermediate === undefined ||
      !mainJumpLabel ||
      !correctionLabel
    ) {
      return null
    }
    return {
      start,
      end,
      intermediate,
      mainJumpLabel,
      correctionLabel,
      label: getString(data, 'label'),
      hideEndValue: getBoolean(data, 'hideEndValue') ?? false,
      mode,
    }
  },

  numberLineLeaps(data, mode) {
    // Annotated number line with N sequential arcs — visualizes the "rijgen"
    // (chained leaps) strategy for mental arithmetic.
    // E.g. 298 + 47 → 298 +2→ 300 +45→ 345.
    const start = getNumber(data, 'start')
    if (start === undefined) return null
    const rawLeaps = getArray(data, 'leaps')
    if (!rawLeaps || rawLeaps.length === 0) return null
    const leaps: Array<{ size: number; label?: string }> = []
    for (const item of rawLeaps) {
      if (!item || typeof item !== 'object') continue
      const obj = item as Record<string, unknown>
      const size = typeof obj.size === 'number' ? obj.size : undefined
      if (size === undefined || !Number.isFinite(size)) continue
      const label = typeof obj.label === 'string' ? obj.label : undefined
      leaps.push({ size, label })
    }
    if (leaps.length === 0) return null
    return {
      start,
      leaps,
      label: getString(data, 'label'),
      hideEndValue: getBoolean(data, 'hideEndValue') ?? false,
      mode,
    }
  },
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Resolve props for a visual component given its type and raw data.
 * Returns null if required data is missing (component should not render).
 */
export function resolveVisualProps(
  type: string,
  data: Record<string, unknown>,
  mode: string,
): Record<string, unknown> | null {
  const resolver = resolvers[type]
  if (!resolver) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[visualPropsResolver] No resolver registered for visual type "${type}". ` +
          `If this type was just added to VISUAL_REGISTRY, also add a resolver here.`,
      )
    }
    return null
  }
  return resolver(data, mode)
}
